import { Component, Mixins, Emit, Prop } from 'vue-property-decorator';
import { MagicConfigMixin, isEscapeKey, getGameCategory, RoutablePage, modalService, ModalSize, formatCurrency, filterGamesPlatforms, OmitFirst, cdnService, isMobileQuery } from '@gig/magic-common';
import { magic } from '@gig/magic-sdk';
import { getters, actions, LOYALTY_NAMESPACE } from '../../../store/modules/loyalty';
import { namespace } from 'vuex-class';
import { getRewardLocalizedString } from '@gig/magic-gamification/dist/util/reward';
import { notificationService, NotificationStyle } from '@gig/magic-messaging';
import { RewardPromoType } from '@gig/magic-sdk/dist/adapter/rewards/reward';
import { IRewardGamesModal } from '@gig/magic-gamification/dist/components/reward-games-modal/reward-games-modal';
import { config } from '../../../services';
import { ISubscription } from '@gig/magic-message-bus/dist/base-bus';
import { IGame } from '@gig/magic-sdk/dist/adapter/games/game';
import { GAMES_LIST } from '@gig/magic-messages';

const loyaltyModule = namespace(LOYALTY_NAMESPACE);
@Component({
    name: 'loyalty-reward-modal',
})
export default class LoyaltyRewardModal extends Mixins(MagicConfigMixin) {

    /**
     * Reward slug
     */
    @Prop({
        type: String,
        required: true,
    })
    rewardId!: string;

    /**
     * Maximum width of the reward header image, used to
     * download the optimal image to show.
     */
    @Prop({ type: Number, default: 380 })
    rewardImageWidth!: number;

    @loyaltyModule.Getter('reward')
    findReward!: ReturnType<typeof getters.reward>;

    @loyaltyModule.Action('claimReward')
    claimReward!: OmitFirst<typeof actions.claimReward>;

    gameplayGames: Map<string, IGame> = new Map<string, IGame>();

    isExchanging: boolean = false;
    
    isClaimingReward: boolean = false;
    
    private isMobile: boolean = false;
    private gamesListSubscription: ISubscription | null = null;

    async created() {
        this.isMobile = await isMobileQuery();
        this.gamesListSubscription = window.eventBus.subscribe<
            Map<string, IGame>
        >(
            GAMES_LIST,
            (e) => {
                this.gameplayGames = e.payload ?? new Map<string, IGame>();
            },
            { replay: true },
        );
    }

    mounted() {
        this.escapeKeyListener = (event: KeyboardEvent) => {
            if (isEscapeKey(event)) {
                this.emitCloseLoyaltyReward();
            }
        };
        document.addEventListener('keyup', this.escapeKeyListener);
    }

    destroyed() {
        this.gamesListSubscription?.unsubscribe();
        if (this.escapeKeyListener) {
            document.removeEventListener('keyup', this.escapeKeyListener);
        }
    }

    get coins() {
        return this.reward?.virtualCurrencyPriceToBuy || 0;
    }

    get reward() {
        return this.findReward(this.rewardId);
    }

    get title() {
        return getRewardLocalizedString(this.reward, 'title');
    }

    get description() {
        return getRewardLocalizedString(this.reward, 'description');
    }

    get content() {
        return getRewardLocalizedString(this.reward, 'content');
    }

    onBuyRewardClick() {
        this.buyReward();
        this.emitCloseLoyaltyReward();
    }

    onClaimRewardClick() {
        this.buyReward(true);
        this.emitCloseLoyaltyReward();
    }

    async buyReward(claim: boolean = false) {
        if (!this.reward || this.isExchanging) {
            return;
        }
        this.isExchanging = true;
        try {
            await magic.methods.rewards.buyTokens({
                purchase: [{ slug: this.reward.slug, number: 1 }],
            });
            if (claim) {
                await this.claim()
            } else {
                notificationService.show(this.$t('loyalty.buy-reward.success'), {
                    style: NotificationStyle.SUCCESS,
                    uniqueID: 'loyalty-buy-reward-success',
                });
            }
        } catch (error) {
            notificationService.show(this.$t('loyalty.buy-reward.error'), {
                style: NotificationStyle.ERROR,
                uniqueID: 'loyalty-buy-reward-error',
            });
        } finally {
            this.isExchanging = false;
        }
    }

    async claim() {
        if (this.isClaimingReward) {
            return;
        }

        const user = magic.session.getUser();

        this.isClaimingReward = true;
        const claim = await this.claimReward({
            reward: this.reward,
            userName: user!.userName,
        });
        this.isClaimingReward = false;

        if (!claim) {
            notificationService.show(this.$t('loyalty.claim-reward.error'), {
                style: NotificationStyle.ERROR,
                uniqueID: 'loyalty-claim-reward-error',
            });
            return;
        } else {
            notificationService.show(this.$t('loyalty.claim-reward.success'), {
                style: NotificationStyle.SUCCESS,
                uniqueID: 'loyalty-claim-reward-success',
            });
        }

        if (this.reward.games) {
            // if the reward has only one game
            // redirect to it
            if (this.reward.games.length === 1 && this.reward.games[0].slug) {
                const game = this.reward.games[0];
                const lang = this.magicConfig.language;
                const cats = game.categories ? game.categories[lang] : null;
                const mainCategory = getGameCategory(
                    cats || [],
                    config.defaultGameCategories
                );
                this.routeTo({
                    route: RoutablePage.GAMEPLAY,
                    params: {
                        gameSlug: game.slug,
                        categorySlug: mainCategory,
                    },
                    meta: {
                        lobbies: game.lobbies,
                    },
                });
            }
            // if the reward has more games
            // show a modal to let the user choose one
            if (this.reward.games.length > 1) {
                const gamesModal: IRewardGamesModal = document.createElement(
                    'magic-reward-games-modal',
                );
                gamesModal.games = filterGamesPlatforms(
                    this.reward.games,
                    this.isMobile,
                );
                gamesModal.defaultCategories = config.defaultGameCategories;
                modalService.open('reward.choose-a-game', gamesModal, {
                    size: ModalSize.LARGE,
                });
            }
        }

        if (this.reward && this.reward.promoType === RewardPromoType.IGC_CASH) {
            notificationService.show(
                this.$t('reward.claimed-reward-notification', {
                    amount: formatCurrency(
                        `${this.reward.euroValue}`,
                        user!.currency,
                    ),
                }),
                { uniqueID: 'reward.claimed-reward-notification' },
            );
        }

        this.onClaim();
    }

    onClaim(): boolean | void {
        return true;
    }

    /**
     * Emitted when user closes the loyalty reward
     */
    @Emit('closeLoyaltyReward')
    emitCloseLoyaltyReward() {
        return null;
    }

    private getImage(retina: boolean = false) {
        if (!this.reward || !this.reward.image) {
            return '';
        }

        const lang = this.magicConfig.language;
        const path = this.reward.image[lang]
            ? this.reward.image[lang]
            : this.reward.image.en;
        return cdnService.getRewardsImageUrl(path, {
            width: this.rewardImageWidth,
            quality: 100,
            retina,
        });
    }

    private escapeKeyListener: null | ((event: KeyboardEvent) => void) = null;
}
